import React, { SelectHTMLAttributes, forwardRef, ReactNode, useId } from 'react';
import { cn } from '@/lib/utils';

type SelectSize = 'sm' | 'md' | 'lg';
type SelectVariant = 'default' | 'filled';

export interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
    options: SelectOption[];
    error?: string;
    label?: string;
    helperText?: string;
    placeholder?: string;
    containerClassName?: string;
    selectSize?: SelectSize;
    variant?: SelectVariant;
    icon?: ReactNode;
}

const sizeClasses: Record<SelectSize, string> = {
    sm: 'input-sm',
    md: '',
    lg: 'input-lg',
};

const Select = forwardRef<HTMLSelectElement, SelectProps>(
    ({
        className = '',
        options,
        error,
        label,
        helperText,
        placeholder,
        containerClassName = '',
        selectSize = 'md',
        variant = 'default',
        icon,
        disabled,
        id: idProp,
        name: nameProp,
        ...props
    }, ref) => {
        const generatedId = useId();
        const selectId = idProp ?? generatedId;
        const selectName = nameProp ?? selectId;

        const selectEl = (
            <select
                ref={ref}
                id={selectId}
                name={selectName}
                disabled={disabled}
                className={cn(
                    'input',
                    sizeClasses[selectSize],
                    variant === 'filled' && 'input-filled',
                    error && 'input-error-state',
                    className,
                )}
                {...props}
            >
                {placeholder && (
                    <option value="" disabled>{placeholder}</option>
                )}
                {options.map((option) => (
                    <option key={option.value} value={option.value} disabled={option.disabled}>
                        {option.label}
                    </option>
                ))}
            </select>
        );

        return (
            <div className={cn('input-wrap', containerClassName)}>
                {label && (
                    <label htmlFor={selectId} className="input-label">
                        {label}
                    </label>
                )}
                {icon ? (
                    <div className="input-group input-group--left-icon">
                        <span className="input-icon">{icon}</span>
                        {selectEl}
                    </div>
                ) : (
                    selectEl
                )}
                {error && <p className="input-error">{error}</p>}
                {helperText && !error && <p className="input-hint">{helperText}</p>}
            </div>
        );
    }
);

Select.displayName = 'Select';

export default Select;
